import { translate, useCurrentCountry, useMatchSmallViewport, useStaticData } from '@avensia/excite';
import { placeholderSrc } from '@avensia/excite-ui';
import { TestIdProps } from '@avensia/test-automation';
import { styled } from '@glitz/react';
import * as React from 'react';
import { SquareImage } from 'webapp/components/image';
import Link from 'webapp/components/Link';
import { EnergyLabel } from 'webapp/EnergyLabel';
import { addSelectItemEventToDataLayer, ListType } from 'webapp/tracking/events/items';
import { createMaxSmallViewportDecorator, createMinLargeViewportDecorator } from 'webapp/viewport';
import { WishlistButton } from 'webapp/components/FavoriteButton';
import { AddToCartButton } from 'webapp/components/AddToCartButton';
import Badge from 'webapp/product/Badge';
import { linkTitle } from 'webapp/product/product-seo-info';
import { NnTestIdProps } from 'webapp/shared/test-ids';
import { Variant } from 'webapp/product/price-variant';
import RatingStars from 'webapp/pages/ProductPage/RatingStars';
import { overflowEllipsisDecorator } from 'webapp/decorators/text';
import { StockStatus } from 'nordicnest/product/StockStatus';
import Price from 'nordicnest/product/Price';
import {
  addSelectRetailMediaSponsoredProductToDataLayer,
  addViewRetailMediaSponsoredProductToDataLayer,
  useRetailMediaViewEventTrigger,
} from 'webapp/tracking/events/retail-media';
import { a11yGrey } from 'webapp/shared/sharedTheme';

type PropType = {
  list_id?: string;
  list_name?: string;
  list_type?: ListType;
  list_pagination_index?: number;
  list_pagination_max?: number;
  product: NordicNest.Api.PartialProductApiModel;
  index?: number;
};

export const ProductCard = ({
  list_id,
  list_name,
  list_type,
  list_pagination_index,
  list_pagination_max,
  product,
  index,
}: PropType) => {
  const scriptSettings = useStaticData<NordicNest.Api.ScriptSettingsApiModel>();
  const isSmallViewport = useMatchSmallViewport();
  const [validationMessage, setValidationMessage] = React.useState<string>();
  const self = React.useRef<HTMLDivElement>(null);
  const isSponsoredProduct = product?.retailMediaSponsoredProductContext?.isSponsoredProduct;

  useRetailMediaViewEventTrigger(
    self,
    addViewRetailMediaSponsoredProductToDataLayer,
    {
      adId: product?.retailMediaSponsoredProductContext?.adId,
      isSponsoredProduct,
    },
    null,
    isSponsoredProduct === true,
  );

  const onClickProductLink = () => {
    addSelectItemEventToDataLayer({
      product,
      index,
      list_id,
      list_name,
      list_type,
      list_pagination_index,
      list_pagination_max,
    });

    if (product?.retailMediaSponsoredProductContext?.isSponsoredProduct) {
      addSelectRetailMediaSponsoredProductToDataLayer(product?.retailMediaSponsoredProductContext?.adId);
    }
  };

  const [button_location, button_name] = list_name?.startsWith('pdp_')
    ? ['pdp', list_name.replace('pdp_', '')]
    : ['plp', list_name ?? 'not_available'];

  const currentCountry = useCurrentCountry();

  return (
    <Card css={noBorderDecorator} ref={self}>
      <ProductLink
        onClick={onClickProductLink}
        title={linkTitle(product)}
        to={product.url}
        linkIdentifier={product.ticket}
        {...TestIdProps.ProductLink}
      >
        <ImageContainer>
          <SquareImage alt={linkTitle(product)} image={product.images[0]} sizes="40vw" preset={'tiny'} />
          {product.energyLabel && <EnergyBadge label={product.energyLabel} small alignRight />}
          <CardBottomSection css={styled({ marginRight: product.energyLabel ? t => t.sharedSpacing.xl : 0 })}>
            {product.availableInCountry ? (
              <StockStatus productCardStyling variantStatus={product.variantStatus} stockStatus={product.stockStatus} />
            ) : (
              <NotAvailable>
                {translate(t => t.productPage.productNotAvailableInCountry, { ['country']: currentCountry.localName })}
              </NotAvailable>
            )}
          </CardBottomSection>
        </ImageContainer>
        <ProductCardInfoWrapper>
          {product?.retailMediaSponsoredProductContext?.isSponsoredProduct &&
            (isSmallViewport || (!isSmallViewport && !scriptSettings.hideReviews)) && (
              <SponsoredProductText>{translate(t => t.product.sponsoredProductText)}</SponsoredProductText>
            )}
          <CardInfoTopWrapper
            css={
              product?.retailMediaSponsoredProductContext?.isSponsoredProduct &&
              (isSmallViewport || (!isSmallViewport && !scriptSettings.hideReviews))
                ? reducedTopMarginDecorator
                : undefined
            }
          >
            <BrandAndSponsoredWrapper>
              <Text>{product.brand}</Text>
              {product?.retailMediaSponsoredProductContext?.isSponsoredProduct &&
                !isSmallViewport &&
                scriptSettings.hideReviews && (
                  <SponsoredProductText css={inlineDecorator}>
                    {translate(t => t.product.sponsoredProductText)}
                  </SponsoredProductText>
                )}
            </BrandAndSponsoredWrapper>
            {!scriptSettings.hideReviews && !isSmallViewport && product.rating > 0 && (
              <RatingStars showRatingCount={false} rating={product.rating} />
            )}
          </CardInfoTopWrapper>
          <Title>{product.displayName}</Title>
          {!scriptSettings.hideReviews && isSmallViewport && product.rating > 0 && (
            <MobileRatingStars showRatingCount={false} rating={product.rating} />
          )}
          {validationMessage && <ValidationMessage>{validationMessage}</ValidationMessage>}
        </ProductCardInfoWrapper>
      </ProductLink>
      <Actions>
        {product.availableInCountry && (
          <ProductPriceWrapper>
            <ProductPrice
              variant={Variant.Compact}
              price={product.price}
              config={product.price}
              useOrigianlPricePrefixSuffix
              productCardStyling
            />
          </ProductPriceWrapper>
        )}
        <Buttons>
          <FavoriteButton
            product={product}
            location={button_location}
            name={button_name}
            {...NnTestIdProps.ProductCardAddToWishlist}
          />
          {(!product.isPackage || product.quickBuyPackage !== null) && product.availableInCountry && (
            <AddToCartButton
              product={product}
              setValidationMessage={setValidationMessage}
              additionalProductAttributes={{
                item_list_id: list_id,
                item_list_name: list_name,
                item_list_index: index,
                ecommerce: { button_location, button_name },
                isSponsoredProduct: product?.retailMediaSponsoredProductContext?.isSponsoredProduct ?? false,
                adId: product?.retailMediaSponsoredProductContext?.adId,
              }}
            />
          )}
        </Buttons>
      </Actions>
      <Badge
        newsFlag={product.useNewsFlag}
        badge={product.price.discountBadge}
        discount={product.price.discountPlp}
        align="left"
      />
      {product.productBadgesContext?.productBadgeIds?.length > 0 && (
        <Badge productBadgesContext={product.productBadgesContext} newsFlag={false} align="right" />
      )}
    </Card>
  );
};

export function LoadingProductCard() {
  return (
    <CardPlaceholder>
      <ImageContainer>
        <PlaceholderImage src={placeholderSrc(400, 400)} />
      </ImageContainer>
      <CardInfoTopWrapper>
        <Text> </Text>
      </CardInfoTopWrapper>
      <Title> </Title>
      <Actions>
        <PricePlaceholder />
      </Actions>
    </CardPlaceholder>
  );
}

const noBorderDecorator = styled({ border: { bottom: { width: 0 } } });

const ValidationMessage = styled.div({
  color: t => t.negative.color,
  fontSize: t => t.sharedFontSize.xxs,
  fontWeight: t => t.fontWeight?.bold,
  margin: { bottom: t => t.spacing.xs },
  display: 'flex',
});

const CardPlaceholder = styled.div({
  border: { bottom: { color: t => t.white.color, width: 'thin', style: 'solid' } },
  backgroundColor: t => t.white.color,
});

const priceDecorator = styled({
  fontSize: t => t.sharedFontSize.xs,
  minHeight: '1.5em',
});

const CardBottomSection = styled.div(
  createMaxSmallViewportDecorator({
    right: t => t.sharedSpacing.xs,
    bottom: t => t.sharedSpacing.xs,
    left: t => t.sharedSpacing.xs,
  }),
  {
    position: 'absolute',
    right: t => t.sharedSpacing.m,
    bottom: t => t.sharedSpacing.m,
    left: t => t.sharedSpacing.m,
    display: 'flex',
  },
);

const ImageContainer = styled.div(
  createMinLargeViewportDecorator({
    padding: {
      top: t => `calc(15% + ${t.sharedSpacing.xxl + t.sharedSpacing.xs}px)`,
      bottom: t => `calc(15% + ${t.sharedSpacing.xl}px)`,
      x: '15%',
    },
  }),
  {
    padding: {
      top: t => `calc(10% + ${t.sharedSpacing.xxl}px)`,
      bottom: t => `calc(10% + ${t.sharedSpacing.l}px)`,
      x: '10%',
    },
    position: 'relative',
    ':after': {
      content: "''",
      backgroundColor: 'rgba(128, 122, 106, 0.06)',
      position: 'absolute',
      right: 0,
      left: 0,
      bottom: 0,
      top: 0,
    },
  },
);

const ProductPriceWrapper = styled.div({
  display: 'flex',
  height: '100%',
  alignItems: 'flex-end',
});

const PricePlaceholder = styled.div(priceDecorator);

const ProductPrice = styled(Price, priceDecorator);

const CardInfoTopWrapper = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  marginTop: t => t.sharedSpacing.m,
  marginBottom: t => t.sharedSpacing.xs,
  gap: t => t.sharedSpacing.xs,
});

const Text = styled.div(overflowEllipsisDecorator, {
  fontSize: t => t.productCard?.brand?.fontSize,
  fontWeight: t => t.productCard?.brand?.fontWeight,
  minHeight: '1.5em',
});

const Title = styled.h2(
  createMinLargeViewportDecorator({
    whiteSpace: 'nowrap',
  }),
  createMaxSmallViewportDecorator({
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
  }),
  {
    fontSize: t => t.productCard?.title?.fontSize,
    fontWeight: t => t.fontWeight?.normal,
    minHeight: '1.5em',
    marginBottom: t => t.sharedSpacing.xs,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    lineHeight: 1.4,
  },
);

const ProductLink = styled(Link, {
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
});

const ProductCardInfoWrapper = styled.div(
  createMaxSmallViewportDecorator({ padding: { x: t => t.sharedSpacing.xs } }),
  {
    padding: { x: t => t.sharedSpacing.m },
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
  },
);

const Card = styled.div({
  color: t => t.productCard?.title?.color,
  position: 'relative',
  backgroundColor: 'white',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
});

const Actions = styled.div(
  createMaxSmallViewportDecorator({
    padding: { x: t => t.sharedSpacing.xs },
  }),
  {
    padding: { x: t => t.sharedSpacing.m, bottom: t => t.sharedSpacing.m },
    display: 'flex',
    alignItems: 'flex-end',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
);

const Buttons = styled.div({
  display: 'flex',
  marginLeft: 'auto',
  gap: 10,
});

const FavoriteButton = styled(WishlistButton, {
  padding: { xy: 0 },
  fontSize: t => t.sharedFontSize.xxs,
  border: { xy: { width: 0 } },
});

const NotAvailable = styled.div(overflowEllipsisDecorator, {
  fontSize: t => t.sharedFontSize.xxs,
  display: 'block',
});

const EnergyBadge = styled(
  EnergyLabel,
  createMinLargeViewportDecorator({
    bottom: 17,
  }),
  {
    position: 'absolute',
    bottom: 11,
    right: 0,
  },
);

const MobileRatingStars = styled(RatingStars, {
  margin: { bottom: t => t.sharedSpacing.xs },
  flexGrow: 1,
  alignItems: 'flex-end',
});

const BrandAndSponsoredWrapper = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: t => t.sharedSpacing.xs,
  flexGrow: 1,
  justifyContent: 'space-between',
});

const inlineDecorator = styled({
  marginTop: 0,
});

const reducedTopMarginDecorator = styled({
  marginTop: t => t.sharedSpacing.xs,
});

const SponsoredProductText = styled.div({
  fontSize: '12px',
  fontWeight: 450,
  color: a11yGrey,
  display: 'flex',
  alignItems: 'center',
  marginTop: t => t.sharedSpacing.m,
});

const PlaceholderImage = styled.img({
  maxWidth: '100%',
  height: 'auto',
});
